import type { ColumnMeta, SchemaSnapshot, TableMeta } from '@/core/engines/intellisenseTypes';
import type {
  MssqlTableEntry,
  MssqlTableSnapshot,
} from '@/features/connections/services/language-services/mssqlTableCache';
import { getCachedMssqlTables } from '@/features/connections/services/language-services/mssqlTableCache';
import { classifyJsonColumn } from '@/features/sql-resolver/json/classify/classifyJsonColumn';
import { detectMssqlIsJsonColumns } from '@/features/sql-resolver/json/classify/checkIsJsonDetection';

/**
 * Pure projection: native MSSQL snapshot → bus `SchemaSnapshot`. Slice A3.5
 * (PRD: Ralph/JsonColumnSemantics/PRD.md § A3.5) extended the native cache
 * with optional `columns` per table; when present we project them into
 * `TableMeta.columns`, otherwise we keep the legacy empty-array shape so
 * cold-cache reads behave as before. Routines (user procs + functions with
 * parameter signatures, fetched by `fetchMssqlRoutines`) are projected
 * verbatim — they drive EXEC-target and dotted-access routine completion.
 * FK edges are projected verbatim from the cache's `foreignKeys` list
 * (PRD: Slice "MSSQL Resolver — Joins + FK"), and so are the per-table
 * `indexes` (`sys.indexes` + `sys.index_columns`), which drive the index-NAME
 * slot of `DROP INDEX ▮ ON t`. The generated-value column flags
 * (`isIdentity` / `isComputed`) come from `sys.columns` and let the resolver
 * demote the columns T-SQL refuses inside an INSERT column list.
 */
export function projectMssqlSnapshot(native: MssqlTableSnapshot): SchemaSnapshot {
  const tables: TableMeta[] = [];
  const schemaSet = new Set<string>();
  for (const entry of native.tables) {
    schemaSet.add(entry.schema);
    tables.push(projectMssqlTableEntry(entry, native.database));
  }
  return {
    engineId: 'mssql',
    connectionId: native.connectionId,
    database: native.database,
    schemas: Array.from(schemaSet).sort(),
    tables,
    routines: native.routines ?? [],
    sequences: native.sequences ?? [],
    foreignKeys: native.foreignKeys,
    serverVersion: native.serverVersion || undefined,
    mssqlCompatLevel:
      typeof native.mssqlCompatLevel === 'number' ? native.mssqlCompatLevel : undefined,
    ...(native.defaultSchema ? { mssqlDefaultSchema: native.defaultSchema } : {}),
    builtAt: native.builtAt,
  };
}

/**
 * Project ONE native table entry into its bus-shaped `TableMeta`. Split out of
 * `projectMssqlSnapshot` for the tiered snapshot load (SLICES.md § S7 item 1):
 * phase 2 merges columns / CHECKs / indexes page by page and re-projects only
 * the tables a page touched, instead of re-running the whole snapshot
 * projection per page.
 */
export function projectMssqlTableEntry(
  entry: MssqlTableEntry,
  database: string,
): TableMeta {
  const checkSet = entry.checkConstraints
    ? new Set<string>(detectMssqlIsJsonColumns(entry.checkConstraints))
    : new Set<string>();
  const columns: ColumnMeta[] = entry.columns
    ? entry.columns.map((c) => {
        const classification = classifyJsonColumn(c.dataType, 'mssql', {
          hasIsJsonCheck: checkSet.has(c.name),
        });
        const base: ColumnMeta = {
          name: c.name,
          dataType: c.dataType,
          nullable: c.nullable,
          isPrimaryKey: c.isPrimaryKey,
          // Only set when true so a snapshot from before the fetch existed
          // (flags absent on the native entry) projects `undefined` — "not
          // known" — rather than a false "definitely insertable".
          ...(c.isIdentity ? { isIdentity: true } : {}),
          ...(c.isComputed ? { isComputed: true } : {}),
          ordinal: c.ordinal,
        };
        return classification.kind === 'not-json'
          ? base
          : { ...base, jsonClassification: classification };
      })
    : [];
  return {
    name: entry.name,
    schema: entry.schema,
    database,
    kind: entry.type === 'view' ? 'view' : 'table',
    columns,
    ...(entry.indexes ? { indexes: entry.indexes } : {}),
  };
}

/**
 * Synchronous read of the bus-shaped snapshot for `(connectionId, database)`.
 * Returns `null` when the underlying MSSQL table cache has no entry for the
 * pair — the orchestrator treats this as "cold" and falls back to ranker-only
 * behaviour.
 */
export function buildSnapshot(
  connectionId: string,
  database: string,
): SchemaSnapshot | null {
  const native = getCachedMssqlTables(connectionId, database);
  if (!native) return null;
  return projectMssqlSnapshot(native);
}
